import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { faExchangeAlt, faRandom } from '@fortawesome/free-solid-svg-icons';
import { EditProps, Warehouse } from '../../models/table-models';
import { ItemQuantity, RedistributeItemQuantityRequest } from '../../models/warehouse-models';
import { redistributeItemQuantity, useGetAllQuantities, useGetWarehouses } from '../../utils/warehouses-hooks';
import { TableRequestParams } from '../../utils/data-hooks';
import { CustomToastContent } from '../../components/CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { CustomButton } from '../../components/CustomButton';
import { AuthContext } from '../../context/AuthContext';
import { Role } from '../../utils/Enums/Roles';

type RedistributeQuantityProps = Omit<EditProps<ItemQuantity>, 'itemToEdit'> & {
    itemToEdit?: ItemQuantity | ItemQuantity[];
    warehouseId: number;
};

export const RedistributeQuantity = ({
    itemToEdit,
    setShowModal,
    refetchEntitiesHook,
    warehouseId,
}: RedistributeQuantityProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const itemsToRedistribute = Array.isArray(itemToEdit) ? itemToEdit : itemToEdit ? [itemToEdit] : [];
    const isBulkRedistribution = itemsToRedistribute.length > 1;
    const { register, handleSubmit, formState, control } = useForm<RedistributeItemQuantityRequest>({
        mode: 'all',
        defaultValues: {
            count: 1,
        },
    });
    const [getWarehouses] = useAsyncResource(useGetWarehouses, {
        'pager.page': 1,
        'pager.itemsPerPage': 1000,
    } as TableRequestParams);
    const warehouses = getWarehouses().items.filter((warehouse: Warehouse) => warehouse.id !== warehouseId);
    const maxCount = !Array.isArray(itemToEdit) ? itemToEdit?.count ?? 0 : 0;
    const quantityLabel = String(t('quantity'));
    const invalidQuantityMessage = String(t('invalidQuantity'));

    const onSubmit = async (formValue: RedistributeItemQuantityRequest) => {
        if (!itemsToRedistribute.length) {
            return;
        }

        await Promise.all(
            itemsToRedistribute.map((item) =>
                item.id
                    ? redistributeItemQuantity({
                          id: item.id,
                          warehouseId: formValue.warehouseId,
                          count: isBulkRedistribution ? item.count : Number(formValue.count),
                      })
                    : Promise.resolve()
            )
        );

        resourceCache(useGetAllQuantities).clear();
        await refetchEntitiesHook();
        setShowModal(false);
        toast.success(<CustomToastContent content={[t('quantityRedistributed')]} />, toastOptionsTopRight);
    };

    return (
        <Form id='form' className='d-flex flex-column align-items-center p-4 w-100' noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className='w-100 mb-3'>
                {isBulkRedistribution ? (
                    <>
                        <div className='fw-bold'>{t('redistributeQuantity')}: {itemsToRedistribute.length}</div>
                        <div className='text-secondary'>
                            {itemsToRedistribute.map((item) => item.passport.passport).join(', ')}
                        </div>
                    </>
                ) : (
                    <>
                        <div className='fw-bold'>{itemsToRedistribute[0]?.passport.passport}</div>
                        <div className='text-secondary'>
                            {t('available')}: {maxCount}
                        </div>
                    </>
                )}
            </div>

            <Form.Group className='d-flex flex-column mb-3 w-100 align-items-start' controlId='warehouseId'>
                <Form.Label>{t('forWarehouse')}</Form.Label>
                <Controller
                    control={control}
                    name='warehouseId'
                    rules={{ required: true }}
                    render={({ field }) => (
                        <>
                            <Select
                                className='w-100'
                                onChange={(newValue) => field.onChange(newValue?.id)}
                                options={warehouses}
                                getOptionLabel={({ warehouseName }) => warehouseName ?? ''}
                                getOptionValue={({ id }) => (id ? id.toString() : '')}
                            />
                            {formState.errors.warehouseId && <p className='input-error'>{'*' + t('choose')}</p>}
                        </>
                    )}
                />
            </Form.Group>

            {!isBulkRedistribution && (
                <Form.Group className='d-flex flex-column mb-2 w-100 align-items-start' controlId='count'>
                    <Form.Label>{t('quantity')}</Form.Label>
                    <Form.Control
                        type='number'
                        min={1}
                        max={maxCount}
                        isInvalid={!!formState.errors.count}
                        {...register('count', {
                            valueAsNumber: true,
                            required: quantityLabel,
                            min: {
                                value: 1,
                                message: invalidQuantityMessage,
                            },
                            max: {
                                value: maxCount,
                                message: invalidQuantityMessage,
                            },
                        })}
                    />
                    {formState.errors.count && (
                        <p className='input-error'>{'*' + (formState.errors.count.message ?? quantityLabel)}</p>
                    )}
                </Form.Group>
            )}
        </Form>
    );
};

export const RedistributeQuantityAction = ({
    item,
    selectedItems = [],
    refetchEntitiesHook,
    warehouseId,
}: {
    item: ItemQuantity;
    selectedItems?: ItemQuantity[];
    refetchEntitiesHook: () => void;
    warehouseId: number;
}) => {
    const [showModal, setShowModal] = React.useState(false);
    const [showChoiceModal, setShowChoiceModal] = React.useState(false);
    const [actionItems, setActionItems] = React.useState<ItemQuantity | ItemQuantity[]>(item);
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: tableT } = useTranslation('translation', { keyPrefix: 'table' });
    const { hasPermission } = React.useContext(AuthContext);
    const selectedItemsWithId = selectedItems.filter((selectedItem) => selectedItem.id);

    const openRedistributionModal = () => {
        setActionItems(selectedItemsWithId.length > 0 ? selectedItemsWithId : item);
        setShowChoiceModal(false);
        setShowModal(true);
    };

    return (
        <>
            <CustomButton
                icon={selectedItemsWithId.length > 0 ? faRandom : faExchangeAlt}
                iconClass='fa-fw'
                onClick={() => setShowChoiceModal(true)}
                className='color-main'
                tooltipTitle={tableT('redistributeQuantity')}
                disabled={!hasPermission([Role.WAREHOUSE, Role.ADMIN])}
            />
            <Modal show={showChoiceModal} onHide={() => setShowChoiceModal(false)} backdrop='static' centered>
                <Modal.Header closeButton>
                    <Modal.Title id='redistribute-quantity-action-choice'>{t('confirm')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItemsWithId.length > 0
                        ? t('confirmRedistributeSelectedQuantities')
                        : t('confirmRedistributeQuantity')}
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button onClick={() => setShowChoiceModal(false)}>{t('noAction')}</Button>
                    <Button className='btn-success' onClick={openRedistributionModal}>
                        {t('ok')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop='static' size='lg' centered>
                <Modal.Header closeButton>
                    <Modal.Title id='redistribute-quantity-title'>{tableT('redistributeQuantity')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <RedistributeQuantity
                        itemToEdit={actionItems}
                        setShowModal={setShowModal}
                        refetchEntitiesHook={refetchEntitiesHook}
                        warehouseId={warehouseId}
                    />
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-between'>
                    <Button onClick={() => setShowModal(false)}>{t('close')}</Button>
                    <Button form='form' type='submit' className='btn-success'>
                        {t('save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
